import { useState } from "react";
import { Dog, Download, ExternalLink, FileText, ShieldAlert, ShieldCheck, ShieldQuestion, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../../lib/api";

const DAY = 86400000;

export function rabiesStatus(expiry) {
  if (!expiry) return { key: "unknown", label: "Expiry not set", cls: "bg-stone-100 text-stone-600", Icon: ShieldQuestion };
  const days = Math.ceil((new Date(`${expiry}T00:00:00`) - new Date()) / DAY);
  if (days < 0) return { key: "expired", label: `Expired ${-days} day${-days === 1 ? "" : "s"} ago`, cls: "bg-red-100 text-red-700", Icon: ShieldAlert };
  if (days <= 30) return { key: "soon", label: `Expires in ${days} day${days === 1 ? "" : "s"}`, cls: "bg-amber-100 text-amber-800", Icon: ShieldAlert };
  return { key: "current", label: "Rabies current", cls: "bg-grass-100 text-grass-700", Icon: ShieldCheck };
}

export default function WaiverDog({ waiverId, dog, testid, canRemove, onSaved, onRemoved, onPdf }) {
  const [value, setValue] = useState(dog.rabiesExpiry || "");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const status = rabiesStatus(dog.rabiesExpiry);
  const dirty = value !== (dog.rabiesExpiry || "");
  const pdfUrl = dog.waiverPdf ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${dog.waiverPdf}` : "";

  const generatePdf = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/waivers/${waiverId}/dogs/${dog.id}/pdf`);
      onPdf(data.waiverPdf);
      toast.success(`Signed waiver PDF created for ${dog.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/waivers/${waiverId}/dogs/${dog.id}`, { rabiesExpiry: value });
      onSaved(value);
      toast.success(value ? `Rabies expiry saved for ${dog.name}` : "Rabies expiry cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${dog.name} from this waiver?`)) return;
    try {
      await api.delete(`/admin/waivers/${waiverId}/dogs/${dog.id}`);
      onRemoved();
      toast.success(`${dog.name} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={testid} className="rounded-2xl border border-lavender-700/10 bg-lavender-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-base font-semibold text-lavender-800" data-testid={`${testid}-name`}>
          <Dog size={16} className="text-lavender-600" />{dog.name}
        </p>
        <div className="flex items-center gap-2">
          <span data-testid={`${testid}-status`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${status.cls}`}>
            <status.Icon size={13} /> {status.label}
          </span>
          {canRemove && (
            <button type="button" onClick={remove} data-testid={`${testid}-remove`} aria-label={`Remove ${dog.name}`} className="grid h-7 w-7 place-items-center rounded-full text-stone-400 transition-colors hover:bg-red-50 hover:text-red-500">
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      <p className="mt-1 text-xs text-stone-400">
        Signed {dog.signed_at ? new Date(dog.signed_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : ""}
        {dog.signature && <> · signature <span className="font-serif italic text-stone-600">{dog.signature}</span></>}
      </p>
      <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {dog.rabiesFile ? (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${dog.rabiesFile}`} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-rabies-link`} className="font-semibold text-lavender-700 hover:underline">
            View uploaded rabies record
          </a>
        ) : (
          <span data-testid={`${testid}-rabies-missing`} className="text-amber-700">Rabies record not uploaded. Must bring proof to the appointment.</span>
        )}
        {dog.waiverPdf ? (
          <button type="button" onClick={() => setPreview(true)} data-testid={`${testid}-pdf-link`} className="inline-flex items-center gap-1 font-semibold text-lavender-700 hover:underline">
            <FileText size={13} /> View signed waiver (PDF)
          </button>
        ) : (
          <button type="button" onClick={generatePdf} disabled={generating} data-testid={`${testid}-pdf-generate`} className="inline-flex items-center gap-1 font-semibold text-stone-500 hover:text-lavender-700 disabled:opacity-60">
            <FileText size={13} /> {generating ? "Generating PDF…" : "Generate signed waiver PDF"}
          </button>
        )}
      </p>
      {dog.waiverPdf && (
        <Dialog open={preview} onOpenChange={setPreview}>
          <DialogContent className="max-w-4xl rounded-2xl" data-testid={`${testid}-pdf-dialog`}>
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Signed waiver — {dog.name}</DialogTitle>
              <DialogDescription>
                Signed by <span className="font-serif italic">{dog.signature}</span>
                {dog.signed_at ? ` on ${new Date(dog.signed_at).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}` : ""}.
              </DialogDescription>
            </DialogHeader>
            <iframe title={`Signed waiver for ${dog.name}`} src={pdfUrl} className="h-[70vh] w-full rounded-lg border border-lavender-700/10 bg-stone-50" data-testid={`${testid}-pdf-frame`} />
            <div className="flex justify-end gap-2">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-pdf-open`} className="inline-flex items-center gap-2 rounded-lg border border-lavender-300 px-4 py-2 text-sm font-semibold text-lavender-800 transition-colors hover:bg-lavender-100">
                <ExternalLink size={14} /> Open in new tab
              </a>
              <a href={pdfUrl} download={`Pawtastic-Cuts-Waiver-${dog.name}.pdf`} data-testid={`${testid}-pdf-download`} className="inline-flex items-center gap-2 rounded-lg bg-lavender-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-lavender-800">
                <Download size={14} /> Download PDF
              </a>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="flex-1 text-xs font-medium text-stone-500">
          Rabies expiry date (from the certificate)
          <input
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid={`${testid}-expiry-input`}
            className="mt-1 w-full rounded-lg border border-lavender-700/15 bg-white px-3 py-2 text-sm text-stone-800 focus:border-lavender-500 focus:outline-none focus:ring-2 focus:ring-lavender-500/30"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          data-testid={`${testid}-expiry-save`}
          className="rounded-lg bg-lavender-700 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-lavender-800 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
